import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AddClientModal from "@/components/AddClientModal";
import DisconnectClientModal from "@/components/DisconnectClientModal";
import BulkModulesModal from "@/components/BulkModulesModal";
import { Page, PageHeader } from "@/components/Bits";
import { ArrowRight, Buildings, MagnifyingGlass, SquaresFour, Table as TableIcon, Funnel, CaretUp, CaretDown, Plus, Plugs, UserCircle, Stack } from "@phosphor-icons/react";

const money0 = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(Number(n || 0));

const CATS = [
  { key: "PREPAYMENT", label: "Prepayments", color: "#F37A24" },
  { key: "ACCRUAL", label: "Accruals", color: "#0E0F3F" },
  { key: "DEFERRED_INCOME", label: "Deferred Income", color: "#00A859" },
  { key: "DEPRECIATION", label: "Depreciation NBV", color: "#F59E0B" },
];

function Sparkline({ data = [], color }) {
  const max = Math.max(...data.map((d) => Math.abs(d)), 1);
  return (
    <div className="flex items-end gap-[3px] h-9" aria-hidden="true">
      {data.map((v, i) => {
        const h = Math.max(3, Math.round((Math.abs(v) / max) * 36));
        return (
          <span key={i} className="w-[5px] rounded-[2px]" style={{ height: `${h}px`, backgroundColor: color, opacity: 0.3 + 0.7 * (Math.abs(v) / max) }} />
        );
      })}
    </div>
  );
}

const TONE_TXT = { navy: "text-navy", negative: "text-negative", warn: "text-warn", muted: "text-slate-600" };
function ReadyStat({ n, label, tone, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-baseline gap-1 font-medium hover:opacity-70 transition-opacity ${TONE_TXT[tone]}`}>
      <span className="font-heading font-bold tabular">{n}</span>
      <span className="text-slate-600 font-normal">{label}</span>
    </button>
  );
}

function EntityCard({ e, onView, onDisconnect, index, members, onAssignManager }) {
  const reconciled = e.recon_status === "reconciled";
  const variance = e.recon_status === "variance";
  const dot = variance ? "bg-warn" : reconciled ? "bg-positive" : "bg-slate-300";
  const dotTitle = variance
    ? `${e.recon_variances} variance${e.recon_variances === 1 ? "" : "s"} · ${e.recon_reconciled}/${e.recon_total} codes reconciled`
    : reconciled
      ? `In balance · ${e.recon_reconciled}/${e.recon_total} codes reconciled`
      : "No reconciliation data yet";
  const attention = e.journals_overdue > 0 || variance;
  return (
    <motion.div data-testid={`portfolio-card-${e.tenant_id}`}
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.05 * index }}
      whileHover={{ y: -4 }}
      className={`card-interactive flex flex-col overflow-hidden ${attention ? "border-l-4 border-l-warn" : ""}`}>
      <div className="px-6 pt-6 pb-4 flex items-center gap-2.5">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot}`} title={dotTitle} data-testid={`portfolio-status-${e.tenant_id}`} />
        <h3 className="font-heading font-bold text-lg text-navy tracking-tight truncate" title={e.tenant_name}>{e.tenant_name}</h3>
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          {e.demo && <span data-testid={`portfolio-demo-${e.tenant_id}`} className="text-[0.6rem] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F37A24]/15 text-[#C2410C]">Demo</span>}
          {attention && <span data-testid={`portfolio-attention-${e.tenant_id}`} className="text-[0.6rem] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-warn/15 text-warn">Needs attention</span>}
          {e.is_active && <span className="text-[0.62rem] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange/10 text-orangeink">Active</span>}
        </div>
      </div>
      <div className="px-6 space-y-4">
        {CATS.map((c) => (
          <div key={c.key} className="flex items-end justify-between gap-3">
            <div>
              <div className="text-slate-600 text-sm">{c.label}</div>
              <div className="font-heading font-bold text-xl text-navy tracking-tight tabular" data-testid={`portfolio-${e.tenant_id}-${c.key}`}>
                {money0(e.balances?.[c.key])}
              </div>
            </div>
            <Sparkline data={e.sparklines?.[c.key] || []} color={c.color} />
          </div>
        ))}
      </div>
      <div className="px-6 py-3.5 mt-5 border-t border-slate-100">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs" data-testid={`portfolio-readiness-${e.tenant_id}`}>
          <ReadyStat n={e.journals_due} label="due" tone={e.journals_due ? "navy" : "muted"}
            onClick={() => onView(e.tenant_id, "/close")} testid={`readiness-due-${e.tenant_id}`} />
          <ReadyStat n={e.journals_overdue} label="overdue" tone={e.journals_overdue ? "negative" : "muted"}
            onClick={() => onView(e.tenant_id, "/close")} testid={`readiness-overdue-${e.tenant_id}`} />
          <ReadyStat n={e.approvals_waiting} label="to approve" tone={e.approvals_waiting ? "warn" : "muted"}
            onClick={() => onView(e.tenant_id, "/approvals")} testid={`readiness-approve-${e.tenant_id}`} />
          <button onClick={() => onView(e.tenant_id, "/reconciliation")} data-testid={`readiness-recon-${e.tenant_id}`}
            className={`inline-flex items-center gap-1.5 font-medium hover:opacity-70 transition-opacity ${reconciled ? "text-positive" : variance ? "text-warn" : "text-slate-600"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
            {reconciled ? "Reconciled" : variance ? "Variance" : "No recon"}
          </button>
        </div>
        <div className="text-xs text-slate-600 mt-1.5" data-testid={`portfolio-meta-${e.tenant_id}`}>
          {e.active_schedules} active schedule{e.active_schedules === 1 ? "" : "s"} · {money0(e.due_this_month)} due this month
        </div>
        {onAssignManager && (
          <div className="mt-2.5 flex items-center gap-2" data-testid={`portfolio-manager-${e.tenant_id}`}>
            <UserCircle size={14} className="text-slate-400 shrink-0" />
            <select value={e.account_manager?.uid || ""} data-testid={`portfolio-manager-select-${e.tenant_id}`}
              onClick={(ev) => ev.stopPropagation()} onChange={(ev) => onAssignManager(e.tenant_id, ev.target.value)}
              className="flex-1 bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-navy focus:border-orange focus:ring-1 focus:ring-orange/20 outline-none">
              <option value="">Unassigned — assign a manager</option>
              {(members || []).map((m) => <option key={m.uid} value={m.uid}>{m.name || m.email}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="border-t border-slate-100 flex items-center justify-between">
        {onDisconnect ? (
          <button onClick={() => onDisconnect(e)} data-testid={`portfolio-disconnect-${e.tenant_id}`}
            className="px-6 py-3.5 flex items-center gap-1.5 text-slate-600 text-sm font-medium hover:text-negative transition-colors">
            <Plugs size={15} /> Disconnect
          </button>
        ) : <span />}
        <button onClick={() => onView(e.tenant_id)} data-testid={`portfolio-view-${e.tenant_id}`}
          className="px-6 py-3.5 flex items-center justify-end gap-1.5 text-orangeink text-sm font-semibold hover:gap-2.5 hover:bg-orange/5 transition-all duration-150">
          View entity <ArrowRight size={15} />
        </button>
      </div>
    </motion.div>
  );
}

function ReconBadge({ e }) {
  const reconciled = e.recon_status === "reconciled";
  const variance = e.recon_status === "variance";
  const cls = variance ? "bg-warn/15 text-warn" : reconciled ? "bg-positive/10 text-positive" : "bg-slate-100 text-slate-600";
  return (
    <span className={`text-[0.62rem] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${cls}`}>
      {variance ? `${e.recon_variances || ""} variance${e.recon_variances === 1 ? "" : "s"}`.trim() : reconciled ? "Reconciled" : "No data"}
    </span>
  );
}

function EntityRow({ e, onView, onDisconnect }) {
  const dot = e.recon_status === "variance" ? "bg-warn" : e.recon_status === "reconciled" ? "bg-positive" : "bg-slate-300";
  return (
    <tr className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer" data-testid={`portfolio-row-${e.tenant_id}`} onClick={() => onView(e.tenant_id)}>
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dot}`} />
          <span className="font-semibold text-navy truncate">{e.tenant_name}</span>
          {e.demo && <span className="text-[0.55rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#F37A24]/15 text-[#C2410C]">Demo</span>}
        </div>
      </td>
      <td className="px-3 py-3.5 text-right tabular text-navy">{money0(e.balances?.PREPAYMENT)}</td>
      <td className="px-3 py-3.5 text-right tabular text-navy">{money0(e.balances?.ACCRUAL)}</td>
      <td className="px-3 py-3.5 text-right tabular text-navy hidden lg:table-cell">{money0(e.balances?.DEFERRED_INCOME)}</td>
      <td className="px-3 py-3.5 text-right tabular text-navy hidden lg:table-cell">{money0(e.due_this_month)}</td>
      <td className="px-3 py-3.5"><ReconBadge e={e} /></td>
      <td className="px-5 py-3.5 text-right">
        <div className="inline-flex items-center gap-3 justify-end">
          <button onClick={(ev) => { ev.stopPropagation(); onView(e.tenant_id); }} data-testid={`portfolio-view-${e.tenant_id}`}
            className="inline-flex items-center gap-1.5 text-orangeink text-sm font-semibold hover:gap-2.5 transition-all">
            View <ArrowRight size={14} />
          </button>
          {onDisconnect && (
            <button onClick={(ev) => { ev.stopPropagation(); onDisconnect(e); }} data-testid={`portfolio-disconnect-${e.tenant_id}`}
              title={`Disconnect ${e.tenant_name}`}
              className="p-1.5 rounded-lg text-slate-300 hover:text-negative hover:bg-negative/10 transition-colors">
              <Plugs size={16} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

const SORT_ACCESSORS = {
  name: (e) => (e.tenant_name || "").toLowerCase(),
  PREPAYMENT: (e) => Number(e.balances?.PREPAYMENT || 0),
  ACCRUAL: (e) => Number(e.balances?.ACCRUAL || 0),
  DEFERRED_INCOME: (e) => Number(e.balances?.DEFERRED_INCOME || 0),
  due: (e) => Number(e.due_this_month || 0),
  recon: (e) => ({ variance: 2, reconciled: 1 }[e.recon_status] ?? 0),
};

function SortTh({ label, sortKey, sort, onSort, align = "left", className = "" }) {
  const active = sort.key === sortKey;
  return (
    <th className={`px-3 py-3 font-semibold overline ${className}`}>
      <button onClick={() => onSort(sortKey)} data-testid={`portfolio-sort-${sortKey}`}
        className={`inline-flex items-center gap-1 hover:text-navy transition-colors ${align === "right" ? "flex-row-reverse" : ""} ${active ? "text-navy" : ""}`}>
        {label}
        <span className="inline-flex flex-col -space-y-1.5">
          <CaretUp size={9} weight="fill" className={active && sort.dir === "asc" ? "text-orangeink" : "text-slate-300"} />
          <CaretDown size={9} weight="fill" className={active && sort.dir === "desc" ? "text-orangeink" : "text-slate-300"} />
        </span>
      </button>
    </th>
  );
}

export default function Portfolio() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("clients_view") || "cards");
  const [query, setQuery] = useState("");
  const [varianceOnly, setVarianceOnly] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [showAdd, setShowAdd] = useState(false);
  const [showBulkModules, setShowBulkModules] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [members, setMembers] = useState([]);

  const reload = () => api.get("/portfolio").then(({ data }) => setData(data)).catch(() => {});

  const assignManager = async (tid, manager_uid) => {
    try {
      await api.put(`/organisations/${tid}/manager`, { manager_uid });
      toast.success(manager_uid ? "Account manager assigned" : "Account manager cleared");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign account manager");
    }
  };

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  useEffect(() => {
    api.get("/portfolio").then(({ data }) => setData(data)).catch(() => setData({ entities: [], count: 0 }));
  }, []);

  useEffect(() => {
    if (isAdmin) api.get("/practice/members").then(({ data }) => setMembers(data.members || [])).catch(() => {});
  }, [isAdmin]);

  const setView = (m) => { setViewMode(m); localStorage.setItem("clients_view", m); };

  const view = async (tid, dest = "/dashboard") => {
    try {
      await api.post("/xero/organisations/activate", { tenant_id: tid });
      localStorage.setItem("lottie_workspace", "org");
      localStorage.setItem("lottie_collapse_nav", "1");
      window.location.assign(dest);
    } catch {
      toast.error("Could not switch entity. Please try again.");
    }
  };

  if (data === null) {
    return <Page><span className="font-mono text-slate-600 text-sm tracking-widest">LOADING_DATA…</span></Page>;
  }

  const n = data.count || 0;
  const q = query.trim().toLowerCase();
  const filtered = (data.entities || []).filter((e) =>
    (!q || (e.tenant_name || "").toLowerCase().includes(q)) &&
    (!varianceOnly || e.recon_status === "variance")
  );
  const varianceCount = (data.entities || []).filter((e) => e.recon_status === "variance").length;

  const acc = SORT_ACCESSORS[sort.key];
  const sorted = acc
    ? [...filtered].sort((a, b) => {
        const av = acc(a), bv = acc(b);
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      })
    : filtered;

  return (
    <Page>
      <PageHeader title="Client Overview" subtitle={`Summary across your ${n} connected ${n === 1 ? "entity" : "entities"}`}>
        {isAdmin && (
          <div className="flex items-center gap-2.5">
            {n > 0 && (
              <button onClick={() => setShowBulkModules(true)} data-testid="bulk-modules-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors duration-200">
                <Stack size={17} weight="fill" /> Manage modules
              </button>
            )}
            <button onClick={() => setShowAdd(true)} data-testid="add-client-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-[#F37A24] text-white hover:bg-navy transition-colors duration-200">
              <Plus size={17} weight="bold" /> Add a client
            </button>
          </div>
        )}
      </PageHeader>

      <AddClientModal open={showAdd} onClose={() => setShowAdd(false)} />
      <BulkModulesModal open={showBulkModules} onClose={() => setShowBulkModules(false)} onDone={reload} />
      <DisconnectClientModal open={!!disconnectTarget} org={disconnectTarget}
        onClose={() => setDisconnectTarget(null)}
        onDone={() => { setDisconnectTarget(null); reload(); window.dispatchEvent(new Event("badges:refresh")); }} />

      {n > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6" data-testid="portfolio-toolbar">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
            <input data-testid="portfolio-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients…"
              className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-navy placeholder:text-slate-600 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none" />
          </div>
          <button onClick={() => setVarianceOnly((v) => !v)} data-testid="portfolio-filter-variances"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${varianceOnly ? "border-warn bg-warn/10 text-warn" : "border-slate-200 text-navy hover:border-warn/50"}`}>
            <Funnel size={15} weight={varianceOnly ? "fill" : "regular"} /> Variances{varianceCount ? ` (${varianceCount})` : ""}
          </button>
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl ml-auto sm:ml-0" data-testid="portfolio-view-toggle">
            <button onClick={() => setView("cards")} data-testid="portfolio-view-cards" title="Card view"
              className={`p-2 rounded-lg transition-all ${viewMode === "cards" ? "bg-white shadow-card text-navy" : "text-slate-600 hover:text-navy"}`}>
              <SquaresFour size={18} weight={viewMode === "cards" ? "fill" : "regular"} />
            </button>
            <button onClick={() => setView("table")} data-testid="portfolio-view-table" title="Table view"
              className={`p-2 rounded-lg transition-all ${viewMode === "table" ? "bg-white shadow-card text-navy" : "text-slate-600 hover:text-navy"}`}>
              <TableIcon size={18} weight={viewMode === "table" ? "fill" : "regular"} />
            </button>
          </div>
        </div>
      )}

      {n > 0 && (
        <div className="flex items-center gap-5 mb-6 text-xs text-slate-600" data-testid="portfolio-legend">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-positive inline-block" /> In balance</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-warn inline-block" /> Variance</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-300 inline-block" /> No data</span>
        </div>
      )}

      {n === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="portfolio-empty">
          <Buildings size={40} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600 text-sm">No Xero organisations connected yet.</p>
          {isAdmin && (
            <button onClick={() => setShowAdd(true)} data-testid="add-client-empty-btn"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-[#F37A24] text-white hover:bg-navy transition-colors">
              <Plus size={17} weight="bold" /> Add a client
            </button>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="portfolio-no-results">
          <MagnifyingGlass size={36} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600 text-sm">No clients match {varianceOnly ? "the variance filter" : `"${query}"`}.</p>
        </div>
      ) : viewMode === "cards" ? (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="portfolio-grid">
          {sorted.map((e, i) => (
            <EntityCard key={e.tenant_id} e={e} onView={view} index={i}
              members={members} onAssignManager={isAdmin ? assignManager : null}
              onDisconnect={isAdmin ? setDisconnectTarget : null} />
          ))}
        </div>
      ) : (
        <div className="card-surface overflow-x-auto" data-testid="portfolio-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-slate-600 border-b border-slate-100">
                <SortTh label="Client" sortKey="name" sort={sort} onSort={toggleSort} />
                <SortTh label="Prepayments" sortKey="PREPAYMENT" sort={sort} onSort={toggleSort} align="right" className="text-right" />
                <SortTh label="Accruals" sortKey="ACCRUAL" sort={sort} onSort={toggleSort} align="right" className="text-right" />
                <SortTh label="Deferred" sortKey="DEFERRED_INCOME" sort={sort} onSort={toggleSort} align="right" className="text-right hidden lg:table-cell" />
                <SortTh label="Due (mo)" sortKey="due" sort={sort} onSort={toggleSort} align="right" className="text-right hidden lg:table-cell" />
                <SortTh label="Reconciliation" sortKey="recon" sort={sort} onSort={toggleSort} />
                <th className="px-5 py-3 font-semibold overline text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((e) => <EntityRow key={e.tenant_id} e={e} onView={view}
                onDisconnect={isAdmin ? setDisconnectTarget : null} />)}
            </tbody>
          </table>
        </div>
      )}
    </Page>
  );
}
